import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Sun, ArrowRight, CheckCircle, Circle, Lightning, Heartbeat,
  CalendarBlank, ChartLineUp, Clock, Fire, PenNib
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import OnboardingWizard from '../components/OnboardingWizard';
import { StreakBadge } from '../components/StreakSystem';
import DemoShowcase from '../components/DemoShowcase';
import FirstLookCelebration from '../components/FirstLookCelebration';
import StoryCapturePrompt from '../components/StoryCapturePrompt';
import WeeklyStrategyDashboardCard from '../components/WeeklyStrategyDashboardCard';

const API = process.env.REACT_APP_BACKEND_URL;

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const getHealthLabel = (score) => {
  if (score >= 70) return { text: 'Strong', color: '#22c55e' };
  if (score >= 50) return { text: 'Good', color: '#f59e0b' };
  if (score >= 30) return { text: 'Needs attention', color: '#f59e0b' };
  return { text: 'Critical', color: '#ef4444' };
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [healthScore, setHealthScore] = useState(null);
  const [usage, setUsage] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekPosts, setWeekPosts] = useState([]);
  const [doneToday, setDoneToday] = useState(() => {
    try {
      const todayKey = new Date().toISOString().slice(0, 10);
      const stored = JSON.parse(localStorage.getItem('scl-priorities-done') || '{}');
      return stored.date === todayKey ? stored.done : [];
    } catch { return []; }
  });

  // 5-second post-onboarding mini-preview (generic, no Launch Kit URL)
  // OR full FirstLookCelebration overlay if visitor came from the
  // Launch Kit teaser with their own brand URL.
  const [showMiniPreview, setShowMiniPreview] = useState(false);
  const [showFirstLook, setShowFirstLook] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem('scl-show-mini-preview') === '1') {
        localStorage.removeItem('scl-show-mini-preview');
        const launchKitUrl = sessionStorage.getItem('scl_launch_kit_url');
        if (launchKitUrl) {
          // Visitor came from the Launch Kit teaser — show the full celebration
          setShowFirstLook(true);
        } else {
          setShowMiniPreview(true);
          const fade = setTimeout(() => setShowMiniPreview(false), 5800);
          return () => clearTimeout(fade);
        }
      }
    } catch {}
  }, []);

  const firstName = (user?.name || 'there').split(' ')[0];

  const togglePriority = (num) => {
    const todayKey = new Date().toISOString().slice(0, 10);
    const next = doneToday.includes(num) ? doneToday.filter(n => n !== num) : [...doneToday, num];
    setDoneToday(next);
    try { localStorage.setItem('scl-priorities-done', JSON.stringify({ date: todayKey, done: next })); } catch {}
  };

  const loadWeek = async () => {
    try {
      const { data } = await axios.get(`${API}/api/calendar/upcoming?days=7`);
      setWeekPosts(data.entries || []);
    } catch { /* ignore */ }
  };

  const markStatus = async (entryId, status) => {
    try {
      await axios.post(`${API}/api/calendar/${entryId}/status`, { status });
      setWeekPosts(prev => prev.map(p => p.entry_id === entryId ? { ...p, status } : p));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: ob } = await axios.get(`${API}/api/onboarding/status`);
        if (!ob.onboarding_completed && !ob.steps.brand_profile) setShowOnboarding(true);
      } catch {}
      const [h, u, s] = await Promise.allSettled([
        axios.get(`${API}/api/health-score`),
        axios.get(`${API}/api/user/usage`),
        axios.get(`${API}/api/streak`),
      ]);
      if (h.status === 'fulfilled') setHealthScore(h.value.data);
      if (u.status === 'fulfilled') setUsage(u.value.data);
      if (s.status === 'fulfilled') setStreak(s.value.data);
      loadWeek();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priorities = [
    { num: 1, task: 'Post a Reel with a curiosity hook today', tool: 'Content Studio', path: '/dashboard/content-studio', time: '~10 mins',
      seed: { contentType: 'reel_script', topic: 'a curiosity hook for today', tone: 'Witty', platform: 'Instagram' } },
    { num: 2, task: 'Write an email to re-engage inactive subscribers', tool: 'Email Campaigns', path: '/dashboard/email', time: '~8 mins',
      seed: { topic: 'Re-engage inactive subscribers', objective: 'win-back', tone: 'Friendly' } },
    { num: 3, task: 'Check what your top competitor posted this week', tool: 'Intelligence', path: '/dashboard/intelligence', time: '~5 mins' },
  ];

  const score = healthScore?.score ?? 62;
  const hl = getHealthLabel(score);
  const streakDays = streak?.current_streak ?? 0;
  const creditsLeft = usage?.credits?.total ?? usage?.total ?? 0;

  // Empty state for new user
  if (!loading && showOnboarding) {
    return (
      <DashboardLayout>
        <OnboardingWizard onComplete={() => setShowOnboarding(false)} />
      </DashboardLayout>
    );
  }

  if (!loading && !healthScore && !usage) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="today-empty">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'rgba(200,240,96,0.06)' }}>
            <Sun size={32} style={{ color: '#c8f060' }} />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>Your daily plan is being built</h2>
          <p className="text-sm max-w-sm mb-6" style={{ color: 'rgba(245,240,232,0.35)' }}>
            Tell us about your business and we'll build your first marketing plan right now.
          </p>
          <button onClick={() => navigate('/dashboard/onboarding')} className="px-6 py-3 rounded-full text-sm font-semibold flex items-center gap-2" style={{ background: '#c8f060', color: '#080808' }}>
            Set up my profile <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* First-look celebration — fires when visitor came from Launch Kit teaser */}
      {showFirstLook && (
        <FirstLookCelebration onDismiss={() => setShowFirstLook(false)} />
      )}

      {/* 5-second post-onboarding mini-preview pinned bottom-right (no Launch Kit URL) */}
      {showMiniPreview && (
        <div data-testid="post-onboarding-preview"
          className="fixed bottom-4 right-4 z-[80] max-w-[360px] w-[calc(100vw-32px)] sm:w-[360px] pointer-events-none"
          style={{
            animation: 'scl-mini-in 0.45s cubic-bezier(0.16,1,0.3,1) both, scl-mini-out 0.6s ease-in 5.2s forwards',
          }}>
          <style>{`
            @keyframes scl-mini-in {
              0% { opacity: 0; transform: translateY(20px) scale(0.95); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
            @keyframes scl-mini-out {
              0% { opacity: 1; transform: translateY(0) scale(1); }
              100% { opacity: 0; transform: translateY(20px) scale(0.95); }
            }
          `}</style>
          <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: '#070707', border: '1px solid rgba(200,241,53,0.22)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(200,241,53,0.06) inset' }}>
            <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#c8f060', boxShadow: '0 0 10px #c8f060' }} />
                <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c8f060', fontWeight: 700 }}>Your team is working</span>
              </div>
              <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 9, color: 'rgba(245,240,232,0.45)', letterSpacing: '0.08em' }}>5s preview</span>
            </div>
            <DemoShowcase compact />
          </div>
        </div>
      )}

      <div data-testid="today-screen" className="space-y-6">
        {/* 1. Greeting */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Instrument Serif', serif" }}>
            {getGreeting()}, {firstName}.
          </h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>Here's your plan for today.</p>
        </motion.div>

        {/* 2. Health Score */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="p-5 rounded-2xl flex items-center gap-5" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="relative w-16 h-16 shrink-0">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke={hl.color} strokeWidth="3" strokeDasharray={`${score} ${100 - score}`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-lg font-bold" style={{ color: hl.color }}>{score}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.25)' }}>Marketing Health</p>
            <p className="text-lg font-bold" style={{ color: hl.color }}>{hl.text}</p>
            {healthScore?.delta_explanation && (
              <p className="text-[11px] mt-1 line-clamp-1" style={{ color: 'rgba(245,240,232,0.4)' }}>{healthScore.delta_explanation}</p>
            )}
          </div>
          <div className="ml-auto hidden sm:block">
            <StreakBadge streak={streakDays} />
          </div>
        </motion.div>

        {/* 2.5 Weekly Strategy summary — surfaces this week's strategist recommendation */}
        <WeeklyStrategyDashboardCard />

        {/* 2.6 Analytics quick-link */}
        <Link to="/dashboard/campaign-analytics" data-testid="dashboard-analytics-link"
          className="flex items-center justify-between px-4 py-3 rounded-2xl transition hover:opacity-95"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.08)' }}>
              <ChartLineUp size={16} weight="fill" style={{ color: '#c8f060' }} />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: '#c8f060' }}>Performance Pulse</p>
              <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>See how your campaigns are landing</p>
            </div>
          </div>
          <ArrowRight size={14} style={{ color: 'rgba(245,240,232,0.5)' }} />
        </Link>

        {/* Customer Story Capture — weekly prompt for users with public blog */}
        <StoryCapturePrompt />

        {/* 3. Today's 3 Priorities */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.2)' }}>Today's priorities</p>
            <p className="text-[10px] font-semibold" style={{ color: doneToday.length === priorities.length ? '#c8f060' : 'rgba(245,240,232,0.3)' }}>{doneToday.length}/{priorities.length} done</p>
          </div>
          {doneToday.length === priorities.length ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="all-done-card"
              className="p-6 rounded-2xl text-center" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.18)' }}>
              <div className="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-3" style={{ background: 'rgba(200,240,96,0.12)' }}>
                <CheckCircle size={24} weight="fill" style={{ color: '#c8f060' }} />
              </div>
              <p className="text-base font-bold mb-1" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>Marketing done for today.</p>
              <p className="text-xs" style={{ color: 'rgba(245,240,232,0.45)' }}>See you tomorrow.</p>
            </motion.div>
          ) : (
            <div className="space-y-2">
              {priorities.map((p, i) => {
                const done = doneToday.includes(p.num);
                return (
                  <motion.div key={p.num} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.06 }}
                    className="p-4 rounded-xl flex items-center gap-3 group transition-all hover:ring-1 hover:ring-white/10"
                    style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)', opacity: done ? 0.55 : 1 }}
                    data-testid={`priority-${p.num}`}>
                    <button onClick={() => togglePriority(p.num)} data-testid={`priority-${p.num}-check`}
                      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all"
                      style={{
                        background: done ? '#c8f060' : 'transparent',
                        border: done ? '1px solid #c8f060' : '1.5px solid rgba(255,255,255,0.18)',
                      }}>
                      {done && <CheckCircle size={14} weight="fill" style={{ color: '#080808' }} />}
                    </button>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(p.path, { state: { seed: p.seed } })}>
                      <p className="text-sm font-medium" style={{ color: '#f5f0e8', textDecoration: done ? 'line-through' : 'none' }}>{p.task}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.25)' }}>{p.tool} &middot; {p.time}</p>
                    </div>
                    <ArrowRight size={14} className="opacity-0 group-hover:opacity-50 transition-opacity cursor-pointer" style={{ color: '#c8f060' }}
                      onClick={() => navigate(p.path, { state: { seed: p.seed } })} />
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* 4. Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { icon: Fire, label: 'Streak', value: `${streakDays} ${streakDays === 1 ? 'day' : 'days'}`, color: '#f59e0b' },
            { icon: PenNib, label: 'Made this month', value: `${usage?.content_created_month ?? 0}`, color: '#c8f060' },
            { icon: CalendarBlank, label: 'Active days', value: `${usage?.active_days_month ?? streakDays}`, color: '#a78bfa' },
            { icon: ChartLineUp, label: 'Competitors', value: `${usage?.competitors_tracked ?? 1}`, color: '#22d3ee' },
            { icon: Lightning, label: 'Credits', value: `${creditsLeft}`, color: '#c8f060' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="p-4 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                <Icon size={16} style={{ color: s.color }} className="mb-2" />
                <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: 'rgba(245,240,232,0.2)' }}>{s.label}</p>
                <p className="text-base font-bold mt-0.5" style={{ color: '#f5f0e8' }}>{s.value}</p>
              </div>
            );
          })}
        </div>

        {/* 5. Content Calendar — This Week (Schedule from Calendar flow) */}
        <div className="p-5 rounded-2xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="this-week-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CalendarBlank size={16} style={{ color: '#c8f060' }} />
              <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.25)' }}>This week ({weekPosts.length})</p>
            </div>
            <Link to="/dashboard/content-calendar" className="text-[10px] font-medium flex items-center gap-1" style={{ color: 'rgba(200,240,96,0.5)' }}>
              View full calendar <ArrowRight size={10} />
            </Link>
          </div>

          {weekPosts.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-xs" style={{ color: 'rgba(245,240,232,0.4)' }}>
                No posts scheduled. Make an Auto Campaign and click "Add to calendar".
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {weekPosts.slice(0, 8).map(p => {
                const isPosted = p.status === 'posted';
                const isSkipped = p.status === 'skipped';
                return (
                  <div key={p.entry_id} className="rounded-lg p-3 flex items-center gap-3 transition-all" data-testid={`week-post-${p.entry_id}`}
                    style={{
                      background: isPosted ? 'rgba(34,197,94,0.04)' : isSkipped ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isPosted ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.05)'}`,
                      opacity: isSkipped ? 0.5 : 1,
                    }}>
                    <div className="text-center shrink-0 w-9">
                      <p className="text-sm font-bold" style={{ color: isPosted ? '#22c55e' : '#c8f060' }}>{new Date(p.date).getDate()}</p>
                      <p className="text-[8px] uppercase" style={{ color: 'rgba(245,240,232,0.3)' }}>{new Date(p.date).toLocaleDateString('en', { weekday: 'short' })}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium line-clamp-1" style={{ color: '#f5f0e8', textDecoration: isSkipped ? 'line-through' : 'none' }}>{p.hook || p.body?.slice(0, 60)}</p>
                      <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.35)' }}>
                        {p.platform} · {p.format?.replace('_', ' ')}
                      </p>
                    </div>
                    {isPosted ? (
                      <span className="text-[9px] font-bold flex items-center gap-1" style={{ color: '#22c55e' }}>
                        <CheckCircle size={11} weight="fill" /> Posted
                      </span>
                    ) : isSkipped ? (
                      <button onClick={() => markStatus(p.entry_id, 'scheduled')} className="text-[9px] font-medium" style={{ color: 'rgba(245,240,232,0.4)' }} data-testid={`undo-skip-${p.entry_id}`}>
                        Undo skip
                      </button>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button onClick={() => markStatus(p.entry_id, 'posted')} data-testid={`mark-posted-${p.entry_id}`}
                          className="px-2 py-1 rounded-md text-[9px] font-bold" style={{ background: 'rgba(34,197,94,0.08)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)' }}>
                          Posted
                        </button>
                        <button onClick={() => markStatus(p.entry_id, 'skipped')} data-testid={`mark-skipped-${p.entry_id}`}
                          className="px-2 py-1 rounded-md text-[9px] font-medium" style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          Skip
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
